import type { MouseEvent } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { checkExists } from 'api/typing';
import classNames from 'classnames';
import clamp from 'lodash/clamp';

import 'components/common/Slider/slider.scss';

const HANDLE_WIDTH = 16;

export function mapSliderValueToRange(
  value: number,
  min: number,
  max: number,
  decimals: number = 0,
): number {
  return Number((value * (max - min) + min).toFixed(decimals));
}

export function mapRangeToSliderValue(value: number, min: number, max: number): number {
  if (min === max) {
    return 0;
  }
  return (value - min) / (max - min);
}

type SliderProps = {
  value: number;
  setValue: (value: number) => void;
  editable?: boolean;
  // The minimum and maximum slider values which correspond to whole increments
  minIncrement?: number;
  maxIncrement?: number;
  increment?: number;
  color?: string;
  handleElement?: React.ReactNode;
};

/**
 * Calculates how far along the slider the mouse input indicates.
 */
function calculateSliderInput(
  horizontalMousePosition: number,
  componentRef: React.RefObject<HTMLDivElement>,
): number | undefined {
  if (!componentRef.current) {
    return undefined;
  }
  const componentBounds = componentRef.current.getBoundingClientRect();

  // Distance from left edge of component to mouse position.
  const mouseDistance = horizontalMousePosition - componentBounds.left;
  // Ratio of the component that the mouse has progressed
  const mouseProgressRatio = mouseDistance / componentBounds.width;
  // Clamp the output to [0, 1]
  const clampedMouseProgressRatio = clamp(mouseProgressRatio, 0, 1);

  return clampedMouseProgressRatio;
}

function roundToNearestBoundary(
  value: number,
  lowerBoundary: number,
  upperBoundary: number,
): number {
  if (value < (lowerBoundary + upperBoundary) / 2) {
    return lowerBoundary;
  }
  return upperBoundary;
}

function roundToNearestIncrement(
  value: number,
  minIncrement: number,
  increment: number,
  maxIncrement: number,
): number {
  // If the value is outside of the whole increment values, either round to the nearest increment or slider limit.
  if (value < minIncrement) {
    return roundToNearestBoundary(value, 0, minIncrement);
  }
  if (value > maxIncrement) {
    return roundToNearestBoundary(value, maxIncrement, 1);
  }

  return minIncrement + Math.round((value - minIncrement) / increment) * increment;
}

export function Slider(props: SliderProps): JSX.Element {
  const {
    value,
    minIncrement,
    maxIncrement,
    increment,
    setValue,
    editable = false,
    color,
    handleElement,
  } = props;
  const [isDragging, setIsDragging] = useState(false);
  const sliderBarRef = useRef<HTMLDivElement>(null);

  // When a slider handle is clicked, if the slider is editable, enable dragging state.
  // Also update the slider value.
  function handleMouseDown(event: MouseEvent<HTMLDivElement>): void {
    if (!editable) {
      return;
    }
    // Prevent drag-selecting even if the mouse moves up/down.
    event.preventDefault();
    setIsDragging(true);
    updateSliderValue(event.clientX);
  }

  // If we have an update function, get the input value and
  // update it. Clamp to the nearest increment if provided.
  const updateSliderValue = useCallback(
    (horizontalMousePosition: number): void => {
      if (!setValue) {
        return;
      }
      const inputValue = calculateSliderInput(horizontalMousePosition, sliderBarRef);
      // If we don't have sufficient information, don't update the value.
      if (inputValue === undefined) {
        return;
      }
      if (checkExists(increment) && checkExists(minIncrement) && checkExists(maxIncrement)) {
        const roundedValue = roundToNearestIncrement(
          inputValue,
          minIncrement,
          increment,
          maxIncrement,
        );
        setValue(clamp(roundedValue, 0, 1));
      } else {
        setValue(inputValue);
      }
    },
    [setValue, increment, minIncrement, maxIncrement],
  );

  // While dragging, trigger slider updates.
  const handleMouseMove = useCallback(
    (event: globalThis.MouseEvent): void => {
      if (!isDragging) {
        return;
      }
      updateSliderValue(event.clientX);
    },
    [updateSliderValue, isDragging],
  );

  // When the mouse is released, disable dragging state
  const handleMouseUp = useCallback((): void => {
    setIsDragging(false);
  }, []);

  // Register event listeners for mouse move and release events.
  // When the component unmounts, remove the event listeners.
  useEffect(
    function registerEventListeners() {
      if (!isDragging) {
        return;
      }

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return (): void => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    },
    [isDragging, handleMouseMove, handleMouseUp],
  );

  return (
    <div className="slider-bar-container" ref={sliderBarRef}>
      {/** For QoL, allow clicking on background to also activate drag (less precise mouse required while intent is preserved). */}
      <div
        className={classNames('slider-bar-background', { editable })}
        onMouseDown={handleMouseDown}
      >
        <div
          className="slider-bar-fill"
          style={{ width: `${value * 100}%`, backgroundColor: color }}
        />
      </div>
      {editable && (
        <div
          className="slider-bar-handle"
          style={{
            left: `calc(${value * 100}% - ${HANDLE_WIDTH / 2}px)`,
            backgroundColor: color,
          }}
          onMouseDown={handleMouseDown}
        >
          {handleElement}
        </div>
      )}
    </div>
  );
}
